import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import {
  ChevronDown,
  Sparkles,
  ShieldCheck,
  RotateCcw,
  History,
  MessageCircle,
  Briefcase,
  PenTool,
  Feather,
  Zap,
  User,
  ArrowRight,
  Check,
  Copy,
  FileText,
  Wand2,
  Download,
  Star,
  Quote,
  ChevronRight,
  Globe,
  Clock,
  Lock,
} from "lucide-react";
import { Button, Badge } from "../components/ui";

/* ─── Animated Counter ─── */
function AnimatedCounter({ target, suffix = "", decimals = 0 }: { target: number; suffix?: string; decimals?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const count = useMotionValue(0);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const controls = animate(count, target, { duration: 2.2, ease: "easeOut" });
          return () => controls.stop();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasAnimated, target, count]);

  useEffect(() => {
    const unsubscribe = count.on("change", (latest) => {
      setDisplay(decimals > 0 ? parseFloat(latest.toFixed(decimals)) : Math.round(latest));
    });
    return unsubscribe;
  }, [count, decimals]);

  return <span ref={ref}>{decimals > 0 ? display.toFixed(decimals) : display}{suffix}</span>;
}

/* ─── Scroll Reveal ─── */
function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Section Header ─── */
function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-16">
      <Reveal><span className="inline-block text-xs font-medium text-[#818cf8] tracking-widest uppercase mb-4">{label}</span></Reveal>
      <Reveal delay={0.05}><h2 className="text-3xl md:text-[2.5rem] font-semibold tracking-tight text-white mb-4">{title}</h2></Reveal>
      {subtitle && <Reveal delay={0.1}><p className="text-white/40 max-w-md mx-auto text-base leading-relaxed">{subtitle}</p></Reveal>}
    </div>
  );
}

/* ─── Hero Section ─── */
function HeroSection() {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.12]" style={{ backgroundImage: "url('/images/bg-abstract-tech.jpg')" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/50 via-[#0a0a0f]/80 to-[#0a0a0f]" />
      <div className="absolute top-[30%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#6366f1]/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 text-center max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Badge className="mb-8 inline-flex items-center gap-1.5 bg-white/[0.04] text-[#a5b4fc] border border-[#6366f1]/20 backdrop-blur-sm px-3 py-1.5 text-xs font-medium rounded-full">
            <Sparkles size={12} /> AI 写作 Humanize 工具
          </Badge>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="text-[3.2rem] md:text-[4.5rem] font-semibold leading-[1.1] tracking-tight mb-6">
          <span className="text-white">让 AI 文案</span><br />
          <span className="bg-gradient-to-r from-[#818cf8] via-[#c084fc] to-[#f472b6] bg-clip-text text-transparent">变成真人写作</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}
          className="text-lg text-white/40 mb-10 max-w-lg mx-auto leading-relaxed">
          告别 AI 味，一键 humanize 你的文字。支持多种写作风格，绕过 AI 检测。
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Button variant="primary" size="lg" className="!bg-white !text-[#0a0a0f] hover:!bg-white/90 font-medium px-6 h-11 rounded-lg transition-all">
            立即体验 <ArrowRight size={16} className="ml-1.5" />
          </Button>
          <Button variant="secondary" size="lg" className="!bg-white/[0.04] !border-white/10 hover:!bg-white/[0.08] hover:!border-white/20 !text-white/70 h-11 px-6 rounded-lg transition-all">
            查看演示
          </Button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 flex items-center justify-center gap-6 text-xs text-white/25">
          <span className="flex items-center gap-1.5"><Check size={12} className="text-[#6366f1]/60" /> 免费试用</span>
          <span className="flex items-center gap-1.5"><Check size={12} className="text-[#6366f1]/60" /> 无需信用卡</span>
          <span className="flex items-center gap-1.5"><Check size={12} className="text-[#6366f1]/60" /> 随时取消</span>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }} className="absolute bottom-10 left-1/2 -translate-x-1/2">
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="text-white/20">
          <ChevronDown size={18} strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ─── How It Works ─── */
function HowItWorksSection() {
  const steps = [
    { icon: Copy, num: "01", title: "粘贴文案", desc: "将 AI 生成的文案粘贴到编辑器中，支持长文本批量处理" },
    { icon: Wand2, num: "02", title: "选择风格", desc: "从 6 种写作风格中选择，或让 AI 自动匹配最佳风格" },
    { icon: FileText, num: "03", title: "一键优化", desc: "点击 Humanize，秒级获得自然流畅、像真人写的文案" },
  ];

  return (
    <section className="py-24 px-4 relative">
      <div className="max-w-5xl mx-auto">
        <SectionHeader label="使用流程" title="三步搞定 AI 文案优化" subtitle="无需复杂设置，粘贴即优化" />
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.12}>
              <div className="relative group">
                {i < 2 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-full h-[1px] bg-gradient-to-r from-white/10 to-transparent" />
                )}
                <div className="relative rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] p-7 transition-all duration-300 hover:bg-white/[0.04]">
                  <div className="text-[#6366f1]/30 text-sm font-mono font-medium mb-4">{s.num}</div>
                  <div className="w-10 h-10 rounded-xl bg-[#6366f1]/10 flex items-center justify-center mb-4">
                    <s.icon size={20} className="text-[#818cf8]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-white/40 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Features Section ─── */
function FeaturesSection() {
  const features = [
    { icon: ShieldCheck, title: "智能去 AI 化", desc: "先进 NLP 模型深度改写，有效绕过 GPTZero、Turnitin 等主流 AI 检测工具。", color: "#6366f1" },
    { icon: RotateCcw, title: "多风格切换", desc: "学术、商务、 casual、创意等多种写作风格，一键切换，适配不同场景需求。", color: "#a855f7" },
    { icon: History, title: "历史记录", desc: "所有改写记录自动云端同步，支持全文搜索、版本对比和一键恢复。", color: "#ec4899" },
    { icon: Globe, title: "多语言支持", desc: "支持中文、英文、日文等多语言文案优化，保持原文语义的准确性。", color: "#6366f1" },
    { icon: Clock, title: "批量处理", desc: "一次可处理上万字长文本，秒级输出结果，大幅提升内容生产效率。", color: "#a855f7" },
    { icon: Lock, title: "隐私安全", desc: "端到端加密传输，处理完成后立即删除原文，绝不保存用户数据。", color: "#ec4899" },
  ];

  return (
    <section className="py-24 px-4 relative">
      <div className="max-w-5xl mx-auto">
        <SectionHeader label="核心功能" title="为什么选择 HumanizeWriter" subtitle="专为内容创作者打造的一站式 AI 文案优化工具" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="group relative rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] p-6 transition-all duration-300 hover:bg-white/[0.04] h-full">
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"
                  style={{ background: `radial-gradient(circle at 50% 0%, ${f.color}10, transparent 70%)` }} />
                <div className="relative z-10">
                  <div className="inline-flex p-2.5 rounded-xl mb-4" style={{ backgroundColor: `${f.color}12` }}>
                    <f.icon size={20} style={{ color: f.color }} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{f.title}</h3>
                  <p className="text-sm text-white/40 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Product Demo Section ─── */
function DemoSection() {
  return (
    <section className="py-24 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.08]" style={{ backgroundImage: "url('/images/bg-cyberpunk-cityscape.jpg')" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0a0a0f]/95 to-[#0a0a0f]" />

      <div className="relative z-10 max-w-4xl mx-auto">
        <SectionHeader label="产品演示" title="看看 Humanize 前后的对比" />

        <Reveal>
          <div className="rounded-2xl bg-[#0d0d12] border border-white/[0.08] overflow-hidden">
            {/* Editor header */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06]">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#ff5f57]/80" />
                <div className="w-3 h-3 rounded-full bg-[#febc2e]/80" />
                <div className="w-3 h-3 rounded-full bg-[#28c840]/80" />
              </div>
              <div className="ml-4 flex gap-4 text-xs text-white/30">
                <span className="text-white/60 border-b border-white/20 pb-3 -mb-3">原文</span>
                <span>Humanize 后</span>
              </div>
            </div>

            {/* Editor body */}
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/[0.06]">
              <div className="p-5">
                <div className="text-xs text-white/25 mb-3 font-mono">Before</div>
                <div className="text-sm text-white/50 leading-relaxed space-y-2">
                  <p>在当今快速发展的数字时代，人工智能已经成为推动社会进步的重要力量。AI 技术正在各个领域展现出巨大的潜力，为人们的生活带来了前所未有的便利。</p>
                  <p>然而，随着 AI 生成内容的普及，如何确保这些内容具有自然的人类写作风格，成为了一个亟待解决的问题。</p>
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#f472b6]/60 bg-[#f472b6]/10 px-2.5 py-1 rounded-full">
                  <ShieldCheck size={12} /> AI 检测风险: 高
                </div>
              </div>
              <div className="p-5 relative">
                <div className="absolute top-0 right-0 p-3">
                  <Badge className="bg-[#6366f1]/10 text-[#818cf8] border-[#6366f1]/20 text-[10px]">已优化</Badge>
                </div>
                <div className="text-xs text-white/25 mb-3 font-mono">After</div>
                <div className="text-sm text-white/70 leading-relaxed space-y-2">
                  <p>说实话，这几年 AI 发展得确实快，方方面面都在渗透进我们的生活。不管是刷短视频推荐，还是办公时的智能助手，AI 带来的便利是实打实的。</p>
                  <p>但问题也来了——现在 AI 写的东西越来越多，怎么让人一眼看不出来是机器写的？这确实是很多创作者头疼的事。</p>
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#4ade80]/60 bg-[#4ade80]/10 px-2.5 py-1 rounded-full">
                  <Check size={12} /> AI 检测风险: 低
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Styles Section ─── */
function StylesSection() {
  const styles = [
    { icon: MessageCircle, name: "轻松随意", desc: "社交媒体、日常沟通", color: "#6366f1" },
    { icon: Briefcase, name: "正式专业", desc: "商务邮件、学术论文", color: "#8b5cf6" },
    { icon: PenTool, name: "创意表达", desc: "营销文案、创意写作", color: "#ec4899" },
    { icon: Feather, name: "简洁凝练", desc: "新闻资讯、要点提取", color: "#6366f1" },
    { icon: Zap, name: "激情澎湃", desc: "演讲稿、动员文案", color: "#ec4899" },
    { icon: User, name: "第一人称", desc: "增强代入感和真实性", color: "#8b5cf6" },
  ];

  return (
    <section className="py-24 px-4 relative">
      <div className="absolute inset-0 opacity-[0.02]"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-transparent to-[#0a0a0f]" />

      <div className="relative z-10 max-w-5xl mx-auto">
        <SectionHeader label="写作风格" title="多种风格，一键切换" subtitle="从轻松随意到正式专业，满足你的所有写作场景" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {styles.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.06}>
              <div className="group flex items-center gap-4 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.12] p-5 transition-all duration-300 hover:bg-white/[0.04] cursor-pointer">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: `${s.color}10` }}>
                  <s.icon size={18} style={{ color: s.color }} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-white">{s.name}</h3>
                  <p className="text-xs text-white/30 mt-0.5">{s.desc}</p>
                </div>
                <ArrowRight size={14} className="text-white/20 group-hover:text-white/40 transition-colors" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ─── */
function TestimonialsSection() {
  const testimonials = [
    { name: "林小雨", role: "自媒体博主", content: "以前发的文章总被读者说'AI味太重'，用了 HumanizeWriter 之后，评论里都在夸我文笔好，根本看不出来是 AI 辅助的。", stars: 5 },
    { name: "张明", role: "研究生", content: "论文降重神器！Turnitin 的 AI 检测从 80% 直接降到 5%，导师完全没看出来，顺利通过了答辩。", stars: 5 },
    { name: "王芳", role: "跨境电商运营", content: "每天要写几十条产品描述，这个工具帮我把 AI 生成的文案变成地道的中文，转化率提升了 30%。", stars: 5 },
  ];

  return (
    <section className="py-24 px-4 relative">
      <div className="max-w-5xl mx-auto">
        <SectionHeader label="用户评价" title="创作者们怎么说" />
        <div className="grid md:grid-cols-3 gap-4">
          {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div className="relative rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.10] p-6 transition-all duration-300 h-full">
                <Quote size={20} className="text-[#6366f1]/20 mb-4" />
                <p className="text-sm text-white/60 leading-relaxed mb-6">{t.content}</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6366f1] to-[#a855f7] flex items-center justify-center text-xs font-medium text-white">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{t.name}</div>
                    <div className="text-xs text-white/30">{t.role}</div>
                  </div>
                </div>
                <div className="absolute top-6 right-6 flex gap-0.5">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} size={12} className="text-[#fbbf24] fill-[#fbbf24]" />
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Stats Section ─── */
function StatsSection() {
  const stats = [
    { value: 10, suffix: "万+", label: "活跃用户" },
    { value: 500, suffix: "万+", label: "处理字数" },
    { value: 99.2, suffix: "%", label: "满意度", decimals: 1 },
    { value: 6, suffix: "种", label: "写作风格" },
  ];

  return (
    <section className="py-20 px-4 relative">
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.06]" style={{ backgroundImage: "url('/images/bg-digital-horizon.jpg')" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0a0a0f]/95 to-[#0a0a0f]" />
      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-1">
                  <AnimatedCounter target={s.value} suffix={s.suffix} decimals={s.decimals ?? 0} />
                </div>
                <div className="text-sm text-white/30">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing Preview ─── */
function PricingPreviewSection() {
  const plans = [
    { name: "免费版", price: "¥0", period: "/月", desc: "适合个人用户体验", features: ["每月 5,000 字额度", "3 种基础风格", "7 天历史记录"], highlight: false },
    { name: "专业版", price: "¥29", period: "/月", desc: "适合内容创作者和专业人士", features: ["每月 50,000 字额度", "全部 6 种风格", "无限历史记录", "优先处理队列", "API 访问权限"], highlight: true },
    { name: "团队版", price: "¥99", period: "/月", desc: "适合企业和团队", features: ["每月 200,000 字额度", "全部功能 + 团队协作", "自定义风格训练", "专属客服支持", "私有化部署选项"], highlight: false },
  ];

  return (
    <section className="py-24 px-4 relative">
      <div className="max-w-5xl mx-auto">
        <SectionHeader label="定价方案" title="选择适合你的方案" subtitle="从免费开始，随时升级" />
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 0.1}>
              <div className={`relative rounded-2xl p-6 h-full flex flex-col ${plan.highlight
                ? "bg-white/[0.04] border border-[#6366f1]/30"
                : "bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.10]"
                } transition-all duration-300`}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#6366f1]/20 text-[#818cf8] border-[#6366f1]/30 text-[10px] px-2.5 py-0.5">最受欢迎</Badge>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-white mb-1">{plan.name}</h3>
                  <p className="text-xs text-white/30">{plan.desc}</p>
                </div>
                <div className="mb-6">
                  <span className="text-3xl font-semibold text-white">{plan.price}</span>
                  <span className="text-white/30 text-sm">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-white/50">
                      <Check size={14} className="text-[#6366f1]/60 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.highlight ? "primary" : "secondary"}
                  size="md"
                  className={`w-full h-10 rounded-lg ${plan.highlight
                    ? "!bg-white !text-[#0a0a0f] hover:!bg-white/90"
                    : "!bg-white/[0.04] !border-white/10 !text-white/70 hover:!bg-white/[0.08]"
                    }`}
                >
                  {plan.price === "¥0" ? "免费开始" : "立即升级"}
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ Section ─── */
function FAQSection() {
  const faqs = [
    { q: "HumanizeWriter 如何绕过 AI 检测？", a: "我们使用先进的对抗性文本生成技术，通过调整句式结构、词汇选择和表达习惯，使 AI 生成的内容更接近人类写作风格，从而有效降低被检测工具识别的概率。" },
    { q: "处理后的内容质量如何保证？", a: "我们的模型经过大量优质人类写作样本训练，在 humanize 的同时保持原文语义完整性和逻辑连贯性，确保输出内容既自然又准确。" },
    { q: "免费版和付费版有什么区别？", a: "免费版每月提供 5,000 字处理额度和 3 种基础风格。付费版提供更大额度、全部 6 种风格、无限历史记录和 API 访问等高级功能。" },
    { q: "我的数据安全吗？", a: "绝对安全。我们采用端到端加密传输，处理完成后立即删除原文，绝不保存或用于模型训练。已通过多项安全合规认证。" },
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-24 px-4 relative">
      <div className="max-w-3xl mx-auto">
        <SectionHeader label="常见问题" title="还有疑问？" />
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.10] transition-all duration-300 overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="text-sm font-medium text-white/80 pr-4">{faq.q}</span>
                  <ChevronRight
                    size={16}
                    className={`text-white/30 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? "rotate-90" : ""}`}
                  />
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: openIndex === i ? "auto" : 0, opacity: openIndex === i ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <p className="px-5 pb-5 text-sm text-white/40 leading-relaxed">{faq.a}</p>
                </motion.div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA Section ─── */
function CTASection() {
  return (
    <section className="py-24 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.08]" style={{ backgroundImage: "url('/images/bg-neural-network.jpg')" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-transparent to-[#0a0a0f]" />
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <Reveal>
          <h2 className="text-3xl md:text-[2.75rem] font-semibold tracking-tight text-white mb-5">
            准备好让文案<br />
            <span className="bg-gradient-to-r from-[#818cf8] to-[#c084fc] bg-clip-text text-transparent">更自然了吗？</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-white/35 mb-10 text-base">加入超过 10 万创作者，开始你的 AI 文案优化之旅</p>
        </Reveal>
        <Reveal delay={0.2}>
          <Button variant="primary" size="lg" className="!bg-white !text-[#0a0a0f] hover:!bg-white/90 font-medium px-8 h-11 rounded-lg transition-all">
            免费开始使用 <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Main Page ─── */
export function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white antialiased selection:bg-[#6366f1]/25">
      <HeroSection />
      <HowItWorksSection />
      <FeaturesSection />
      <DemoSection />
      <StylesSection />
      <TestimonialsSection />
      <StatsSection />
      <PricingPreviewSection />
      <FAQSection />
      <CTASection />
      <div className="h-16" />
    </div>
  );
}

export default LandingPage
